/**
 * App root — React Router + MUI ThemeProvider + React Query.
 */

import { lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { QueryClientProvider } from "@tanstack/react-query";
import { darkTheme, lightTheme } from "@/styles/theme";
import { useUiStore } from "@/stores/uiStore";
import { useAuthStore } from "@/stores/authStore";
import { useResponsiveTableLabels } from "@/hooks/useResponsiveTableLabels";
import { queryClient } from "@/services/queryClient";
import "@/styles/global.css";

import PrivateRoute from "@/components/auth/PrivateRoute";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import ToastContainer from "@/components/common/ToastContainer";
import PageSuspense from "@/components/common/PageSuspense";
import LoginPage from "@/pages/LoginPage";

const MainLayout = lazy(() => import("@/components/layout/MainLayout"));
const ProjectsPage = lazy(() => import("@/pages/ProjectsPage"));
const ProjectDetailPage = lazy(() => import("@/pages/ProjectDetailPage"));
const ProjectPrintPage = lazy(() => import("@/pages/ProjectPrintPage"));
const ReportsPage = lazy(() => import("@/pages/ReportsPage"));
const CatalogPage = lazy(() => import("@/pages/CatalogPage"));
const ClientsPage = lazy(() => import("@/pages/ClientsPage"));
const UsersPage = lazy(() => import("@/pages/UsersPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const ForcePasswordChangePage = lazy(() => import("@/pages/ForcePasswordChangePage"));

export default function App() {
  const sessionIdentity = useAuthStore((s) => `${s.user?.id ?? ""}:${s.user?.role ?? ""}`);
  const themeMode = useUiStore((s) => s.themeMode);
  const theme = themeMode === "dark" ? darkTheme : lightTheme;
  useResponsiveTableLabels();

  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("data-mui-color-scheme", themeMode);
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <PageSuspense>
              <Routes>
                <Route path="/login" element={<LoginPage />} />

                <Route element={<PrivateRoute />}>
                  <Route path="/change-password" element={<ForcePasswordChangePage />} />

                  {/* Print view — no app chrome */}
                  <Route element={<PrivateRoute roles={["admin", "manager"]} />}>
                    <Route path="/projects/:id/print" element={<ProjectPrintPage />} />
                  </Route>

                  <Route element={<MainLayout key={sessionIdentity} />}>
                    <Route path="/" element={<Navigate to="/projects" replace />} />
                    <Route path="/projects" element={<ProjectsPage />} />
                    <Route path="/projects/:id" element={<ProjectDetailPage />} />
                    <Route path="/reports" element={<ReportsPage />} />
                    <Route path="/profile" element={<ProfilePage />} />

                    <Route element={<PrivateRoute roles={["admin", "manager"]} />}>
                      <Route path="/catalog" element={<CatalogPage />} />
                      <Route path="/clients" element={<ClientsPage />} />
                      <Route path="/users" element={<UsersPage />} />
                    </Route>

                    <Route path="*" element={<NotFoundPage />} />
                  </Route>
                </Route>
              </Routes>
            </PageSuspense>
          </BrowserRouter>
          <ToastContainer />
        </ErrorBoundary>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
